import React, { useMemo } from "react";
import { ExternalLink, Youtube } from "lucide-react";
import SEO from "@/components/site/SEO";
import { useLanguage } from "@/i18n/LanguageContext";
import { getLiveMediaInfo, resolveMediaUrl } from "@/lib/api";
import { useCachedGet } from "@/hooks/useCachedGet";
import PageSkeleton from "@/components/site/PageSkeleton";

const formatDate = (iso, locale) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale || "id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const Live = () => {
  const { dict, locale } = useLanguage();
  const copy = dict.live;
  const { data, ready } = useCachedGet("/lives", { published: true });
  const items = Array.isArray(data) ? data : [];

  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = formatDate(item.posted_at || item.created_at, locale);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return Array.from(map.entries());
  }, [items, locale]);

  if (!ready) return <PageSkeleton />;

  return (
    <div>
      <SEO title={copy.seoTitle} description={copy.seoDesc} />
      <section className="pt-12 pb-8 bg-[#fff5f5]">
        <div className="container-wide max-w-4xl">
          <div className="text-xs uppercase tracking-[0.22em] text-maroon-700 font-bold">{copy.overline}</div>
          <h1 className="mt-3 text-4xl md:text-5xl font-display font-black text-zinc-900 tracking-tighter">{copy.title}</h1>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed">{copy.lead}</p>
        </div>
      </section>

      <section className="pb-16 md:pb-24 bg-[#fff5f5]">
        <div className="container-wide max-w-5xl">
          {grouped.length === 0 && (
            <div className="bg-white rounded-3xl border border-zinc-100 p-12 text-center text-zinc-500">
              <Youtube className="mx-auto mb-3 text-maroon-700" size={36} />
              {copy.empty}
            </div>
          )}

          <div className="space-y-12">
            {grouped.map(([dateLabel, streams]) => (
              <div key={dateLabel}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="h-px flex-1 bg-maroon-100" />
                  <div className="px-4 py-1.5 rounded-full bg-white border border-maroon-100 text-sm font-semibold text-maroon-800">
                    {dateLabel}
                  </div>
                  <div className="h-px flex-1 bg-maroon-100" />
                </div>
                <div className="space-y-6">
                  {streams.map((s) => {
                    const info = getLiveMediaInfo(s.youtube_url);
                    const openLabel = info?.platform === "tiktok" ? copy.openTiktok : copy.openYoutube;
                    return (
                      <article key={s.id} className="bg-white rounded-3xl border border-zinc-100 overflow-hidden shadow-sm">
                        <div className="aspect-video bg-zinc-900 relative">
                          {info?.embed ? (
                            <iframe
                              title={s.title}
                              src={info.embed}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                            />
                          ) : (
                            <div className="w-full h-full relative flex items-center justify-center">
                              {s.thumbnail ? (
                                <img
                                  src={resolveMediaUrl(s.thumbnail)}
                                  alt=""
                                  className="absolute inset-0 w-full h-full object-cover opacity-80"
                                />
                              ) : null}
                              <div className="relative z-10 text-center px-6">
                                <div className="inline-flex px-3 py-1 rounded-full bg-white/95 text-zinc-900 text-xs font-bold uppercase tracking-wider mb-3">
                                  {info?.label || "Live"}
                                </div>
                                <a
                                  href={s.youtube_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white text-sm font-semibold hover:bg-maroon-900 transition"
                                >
                                  {openLabel} <ExternalLink size={14} />
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-5 md:p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${info?.platform === "tiktok" ? "bg-zinc-900 text-white" : "bg-red-100 text-red-700"}`}>
                              {info?.label || "Live"}
                            </span>
                          </div>
                          <h2 className="text-xl md:text-2xl font-display font-bold text-zinc-900">{s.title}</h2>
                          {s.description && (
                            <p className="mt-2 text-zinc-600 leading-relaxed">{s.description}</p>
                          )}
                          <a
                            href={s.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-maroon-800 hover:underline"
                          >
                            {openLabel} <ExternalLink size={14} />
                          </a>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Live;
